import {createRecipePlan} from '../recipes/recipe-process.js';
import {createSmokerCapabilities,CapabilityAnswer} from '../smokehouse/smoker-capabilities.js';
import {createWoodInventoryItem} from '../smokehouse/wood-inventory.js';
import {createWoodProductGuidance,rankWoodGuidance} from '../smokehouse/wood-product-guidance.js';

export const PlannerCompatibilityStatus=Object.freeze({
  COMPATIBLE:'compatible',
  INCOMPATIBLE:'incompatible',
  UNKNOWN:'unknown',
  REVIEW:'review',
  INFO:'info'
});

const STATUSES=new Set(Object.values(PlannerCompatibilityStatus));

function required(value,label,max=180){
  const text=String(value??'').trim();
  if(!text)throw new TypeError(label+' is required');
  if(text.length>max)throw new TypeError(label+' is too long');
  return text;
}

function optionalBoolean(value,label){
  if(value==null||value==='')return null;
  if(typeof value!=='boolean')throw new TypeError(label+' must be boolean when provided');
  return value;
}

function optionalNumber(value,label){
  if(value==null||value==='')return null;
  const number=Number(value);
  if(!Number.isFinite(number))throw new TypeError(label+' must be numeric');
  return number;
}

function stableKey(value,label){
  const key=String(value??'').trim().toLowerCase();
  if(!/^[a-z][a-z0-9_]{1,63}$/.test(key))throw new TypeError('Invalid '+label);
  return key;
}

function uniqueKeys(values,label){
  const result=[];
  const seen=new Set();
  for(const value of values||[]){
    const key=stableKey(value,label);
    if(!seen.has(key)){
      seen.add(key);
      result.push(key);
    }
  }
  return Object.freeze(result);
}

function stageRequirement({
  stageId,
  stageType,
  label=null,
  targetChamberCelsius=null,
  minChamberCelsius=null,
  maxChamberCelsius=null
}={}){
  const target=optionalNumber(targetChamberCelsius,'targetChamberCelsius');
  const min=optionalNumber(minChamberCelsius,'minChamberCelsius');
  const max=optionalNumber(maxChamberCelsius,'maxChamberCelsius');
  if(min!=null&&max!=null&&min>max)throw new TypeError('stage min temperature cannot exceed max');
  if(target!=null&&min!=null&&target<min)throw new TypeError('stage target cannot be below min');
  if(target!=null&&max!=null&&target>max)throw new TypeError('stage target cannot exceed max');
  return Object.freeze({
    stageId:required(stageId,'stageId'),
    stageType:required(stageType,'stageType',80),
    label:String(label??stageType).trim()||String(stageType),
    targetChamberCelsius:target,
    minChamberCelsius:min,
    maxChamberCelsius:max
  });
}

export function createPlannerCompatibilityRequirements({
  stageRequirements=[],
  coldSmokingRequired=null,
  requestedWoodKeys=[],
  productCategories=[]
}={}){
  if(!Array.isArray(stageRequirements))throw new TypeError('stageRequirements must be an array');
  return Object.freeze({
    stageRequirements:Object.freeze(stageRequirements.map(stageRequirement)),
    coldSmokingRequired:optionalBoolean(coldSmokingRequired,'coldSmokingRequired'),
    requestedWoodKeys:uniqueKeys(requestedWoodKeys,'requested wood key'),
    productCategories:uniqueKeys(productCategories,'product category')
  });
}

function finding({
  code,
  status,
  message,
  stageId=null,
  woodKey=null,
  context={},
  overridable=false,
  alternatives=[]
}={}){
  const state=String(status??'').trim().toLowerCase();
  if(!STATUSES.has(state))throw new TypeError('Unsupported compatibility status');
  return Object.freeze({
    code:required(code,'finding code',100),
    status:state,
    message:required(message,'finding message',600),
    stageId:stageId==null?null:required(stageId,'stageId'),
    woodKey:woodKey==null?null:stableKey(woodKey,'woodKey'),
    context:Object.freeze({...context}),
    overridable:Boolean(overridable),
    alternatives:Object.freeze((alternatives||[]).map(item=>Object.freeze({...item})))
  });
}

function temperatureFinding(requirement,capabilities){
  const values=[
    requirement.minChamberCelsius,
    requirement.targetChamberCelsius,
    requirement.maxChamberCelsius
  ].filter(value=>value!=null);

  if(!values.length){
    return finding({
      code:'STAGE_TEMPERATURE_NOT_SPECIFIED',
      status:PlannerCompatibilityStatus.UNKNOWN,
      message:'Etap „'+requirement.label+'” nie ma jawnego wymagania temperatury — sprawdź ręcznie.',
      stageId:requirement.stageId,
      context:{stageType:requirement.stageType},
      overridable:true
    });
  }

  const requiredMin=Math.min(...values);
  const requiredMax=Math.max(...values);
  const knownMin=capabilities.minTemperatureCelsius;
  const knownMax=capabilities.maxTemperatureCelsius;

  if(knownMin!=null&&requiredMin<knownMin){
    return finding({
      code:'STAGE_TEMPERATURE_BELOW_SMOKER_MIN',
      status:PlannerCompatibilityStatus.INCOMPATIBLE,
      message:'Etap „'+requirement.label+'” wymaga temperatury poniżej znanego minimum wędzarni.',
      stageId:requirement.stageId,
      context:{
        stageType:requirement.stageType,
        requiredMinCelsius:requiredMin,
        requiredMaxCelsius:requiredMax,
        smokerMinCelsius:knownMin,
        smokerMaxCelsius:knownMax
      },
      overridable:true
    });
  }

  if(knownMax!=null&&requiredMax>knownMax){
    return finding({
      code:'STAGE_TEMPERATURE_ABOVE_SMOKER_MAX',
      status:PlannerCompatibilityStatus.INCOMPATIBLE,
      message:'Etap „'+requirement.label+'” wymaga temperatury powyżej znanego maksimum wędzarni.',
      stageId:requirement.stageId,
      context:{
        stageType:requirement.stageType,
        requiredMinCelsius:requiredMin,
        requiredMaxCelsius:requiredMax,
        smokerMinCelsius:knownMin,
        smokerMaxCelsius:knownMax
      },
      overridable:true
    });
  }

  if(knownMin==null||knownMax==null){
    return finding({
      code:'SMOKER_TEMPERATURE_RANGE_INCOMPLETE',
      status:PlannerCompatibilityStatus.UNKNOWN,
      message:'Zakres temperatur wędzarni jest niepełny — etap „'+requirement.label+'” wymaga ręcznego sprawdzenia.',
      stageId:requirement.stageId,
      context:{
        stageType:requirement.stageType,
        requiredMinCelsius:requiredMin,
        requiredMaxCelsius:requiredMax,
        smokerMinCelsius:knownMin,
        smokerMaxCelsius:knownMax
      },
      overridable:true
    });
  }

  return finding({
    code:'STAGE_TEMPERATURE_SUPPORTED',
    status:PlannerCompatibilityStatus.COMPATIBLE,
    message:'Zakres temperatur dla etapu „'+requirement.label+'” mieści się w możliwościach wędzarni.',
    stageId:requirement.stageId,
    context:{
      stageType:requirement.stageType,
      requiredMinCelsius:requiredMin,
      requiredMaxCelsius:requiredMax,
      smokerMinCelsius:knownMin,
      smokerMaxCelsius:knownMax
    }
  });
}

function coldSmokingFinding(requirement,capabilities){
  if(requirement.coldSmokingRequired!==true)return null;

  if(capabilities.coldSmoking===CapabilityAnswer.NO){
    return finding({
      code:'COLD_SMOKING_NOT_SUPPORTED',
      status:PlannerCompatibilityStatus.INCOMPATIBLE,
      message:'Plan jawnie wymaga zimnego wędzenia, a wybrana wędzarnia jest oznaczona jako nieobsługująca tej funkcji.',
      context:{coldSmoking:capabilities.coldSmoking},
      overridable:true
    });
  }

  if(capabilities.coldSmoking===CapabilityAnswer.UNKNOWN){
    return finding({
      code:'COLD_SMOKING_CAPABILITY_UNKNOWN',
      status:PlannerCompatibilityStatus.UNKNOWN,
      message:'Plan wymaga zimnego wędzenia, ale możliwości wybranej wędzarni nie są znane — sprawdź ręcznie.',
      context:{coldSmoking:capabilities.coldSmoking},
      overridable:true
    });
  }

  return finding({
    code:'COLD_SMOKING_SUPPORTED',
    status:PlannerCompatibilityStatus.COMPATIBLE,
    message:'Wybrana wędzarnia jest oznaczona jako obsługująca zimne wędzenie.',
    context:{
      coldSmoking:capabilities.coldSmoking,
      coldSmokeMaxCelsius:capabilities.coldSmokeMaxCelsius
    }
  });
}

function temperatureControlFinding(capabilities){
  return finding({
    code:'TEMPERATURE_CONTROL_CONTEXT',
    status:PlannerCompatibilityStatus.INFO,
    message:capabilities.temperatureControl==='unknown'
      ?'Typ sterowania temperaturą wędzarni nie jest znany; Planner nie używa tego jako blokady.'
      :'Sterowanie temperaturą: '+capabilities.temperatureControl+'. Informacja ma charakter kontekstowy i nie blokuje planu.',
    context:{
      temperatureControl:capabilities.temperatureControl,
      controlPrecisionCelsius:capabilities.controlPrecisionCelsius
    }
  });
}

function normalizeInventory(items){
  if(items==null)return null;
  if(!Array.isArray(items))throw new TypeError('woodInventory must be an array or null');
  return Object.freeze(items.map(createWoodInventoryItem));
}

function activeAvailableWoodKeys(inventory){
  if(inventory==null)return null;
  return new Set(
    inventory
      .filter(item=>!item.deletedAt&&!item.archivedAt&&item.available)
      .map(item=>item.catalogueKey)
      .filter(Boolean)
  );
}

function canonicalGuidance(item){
  if(item?.isGuidance===true&&item?.guidanceOnly===true&&item?.rationales){
    return createWoodProductGuidance({
      woodKey:item.woodKey,
      productKey:item.productKey,
      guidanceLevel:item.guidanceLevel,
      confidenceLevel:item.confidenceLevel,
      rationalePl:item.rationales.pl,
      rationaleEn:item.rationales.en,
      sourceName:item.sourceName,
      sourceVersion:item.sourceVersion,
      guidanceVersion:item.guidanceVersion
    });
  }
  return createWoodProductGuidance(item);
}

function controlledAlternatives({
  requestedWoodKey,
  availableWoodKeys,
  productCategories,
  guidance
}={}){
  if(!availableWoodKeys||!productCategories.length||!Array.isArray(guidance)||!guidance.length){
    return Object.freeze([]);
  }

  const relevant=rankWoodGuidance(guidance.map(canonicalGuidance)).filter(item=>
    item.woodKey!==requestedWoodKey
    &&availableWoodKeys.has(item.woodKey)
    &&productCategories.includes(item.productKey)
  );

  const grouped=new Map();
  for(const item of relevant){
    const existing=grouped.get(item.woodKey)||{
      woodKey:item.woodKey,
      matchedProductCategories:[],
      guidanceLevel:item.guidanceLevel,
      confidenceLevel:item.confidenceLevel,
      sourceName:item.sourceName||null,
      sourceVersion:item.sourceVersion||null,
      guidanceVersion:item.guidanceVersion||null,
      guidanceOnly:true
    };
    if(!existing.matchedProductCategories.includes(item.productKey)){
      existing.matchedProductCategories.push(item.productKey);
    }
    grouped.set(item.woodKey,existing);
  }

  return Object.freeze([...grouped.values()].map(value=>Object.freeze({
    ...value,
    matchedProductCategories:Object.freeze([...value.matchedProductCategories])
  })));
}

function woodFindings(requirement,{inventory,guidance}={}){
  if(!requirement.requestedWoodKeys.length)return [];
  const available=activeAvailableWoodKeys(inventory);

  if(available==null){
    return requirement.requestedWoodKeys.map(woodKey=>finding({
      code:'WOOD_INVENTORY_UNKNOWN',
      status:PlannerCompatibilityStatus.UNKNOWN,
      message:'Plan wymaga drewna „'+woodKey+'”, ale stan zapasów nie jest dostępny — sprawdź ręcznie.',
      woodKey,
      overridable:true
    }));
  }

  return requirement.requestedWoodKeys.map(woodKey=>{
    if(available.has(woodKey)){
      return finding({
        code:'REQUESTED_WOOD_AVAILABLE',
        status:PlannerCompatibilityStatus.COMPATIBLE,
        message:'Wymagane drewno „'+woodKey+'” jest oznaczone jako dostępne.',
        woodKey
      });
    }

    const alternatives=controlledAlternatives({
      requestedWoodKey:woodKey,
      availableWoodKeys:available,
      productCategories:requirement.productCategories,
      guidance
    });
    return finding({
      code:'REQUESTED_WOOD_UNAVAILABLE',
      status:PlannerCompatibilityStatus.REVIEW,
      message:alternatives.length
        ?'Wymaganego drewna „'+woodKey+'” nie ma w zapasach. Dostępne są wyłącznie alternatywy poparte kontrolowaną wskazówką smakową.'
        :'Wymaganego drewna „'+woodKey+'” nie ma w zapasach; brak kontrolowanej podstawy do zasugerowania zamiennika.',
      woodKey,
      overridable:true,
      alternatives
    });
  });
}

function overallStatus(findings){
  if(findings.some(item=>item.status===PlannerCompatibilityStatus.INCOMPATIBLE)){
    return PlannerCompatibilityStatus.INCOMPATIBLE;
  }
  if(findings.some(item=>item.status===PlannerCompatibilityStatus.UNKNOWN)){
    return PlannerCompatibilityStatus.UNKNOWN;
  }
  if(findings.some(item=>item.status===PlannerCompatibilityStatus.REVIEW)){
    return PlannerCompatibilityStatus.REVIEW;
  }
  return PlannerCompatibilityStatus.COMPATIBLE;
}

export function validatePlannerCompatibility({
  requirements,
  smokerCapabilities,
  woodInventory=null,
  woodGuidance=[]
}={}){
  const requirement=createPlannerCompatibilityRequirements(requirements);
  const capabilities=smokerCapabilities==null?null:createSmokerCapabilities(smokerCapabilities);
  const inventory=normalizeInventory(woodInventory);
  if(!Array.isArray(woodGuidance))throw new TypeError('woodGuidance must be an array');

  const findings=capabilities
    ?[
      ...requirement.stageRequirements.map(stage=>temperatureFinding(stage,capabilities)),
      coldSmokingFinding(requirement,capabilities),
      temperatureControlFinding(capabilities),
      ...woodFindings(requirement,{inventory,guidance:woodGuidance})
    ].filter(Boolean)
    :[
      finding({
        code:'SMOKER_NOT_SELECTED',
        status:PlannerCompatibilityStatus.UNKNOWN,
        message:'Nie wybrano wędzarni do sprawdzenia planu — zgodność sprzętową trzeba sprawdzić ręcznie.',
        overridable:true
      }),
      ...woodFindings(requirement,{inventory,guidance:woodGuidance})
    ];

  return Object.freeze({
    status:overallStatus(findings),
    smokerId:capabilities?.smokerId||null,
    findings:Object.freeze(findings),
    canProceed:true,
    requiresReview:findings.some(item=>
      [PlannerCompatibilityStatus.INCOMPATIBLE,PlannerCompatibilityStatus.UNKNOWN,PlannerCompatibilityStatus.REVIEW]
        .includes(item.status)
    )
  });
}

export function plannerCompatibilityRequirementsFromRecipe(plan,{
  coldSmokingRequired=null,
  productCategories=[]
}={}){
  const value=createRecipePlan(plan);
  const stages=new Map(value.stages.map(stage=>[stage.id,stage]));
  return createPlannerCompatibilityRequirements({
    stageRequirements:value.stageTargets.map(target=>{
      const stage=stages.get(target.recipeStageId);
      return {
        stageId:target.recipeStageId,
        stageType:stage?.typeKey||'unknown',
        label:stage?.nameOverride||stage?.typeKey||'Etap',
        targetChamberCelsius:target.targetChamberCelsius,
        minChamberCelsius:target.minChamberCelsius,
        maxChamberCelsius:target.maxChamberCelsius
      };
    }),
    coldSmokingRequired,
    requestedWoodKeys:value.woods.map(wood=>wood.woodKey),
    productCategories
  });
}

export function plannerCompatibilityRequirementsFromSessionBundle(bundle={},{
  coldSmokingRequired=null,
  productCategories=[]
}={}){
  const session=bundle.session;
  if(!session?.id)throw new TypeError('Session bundle is required');
  const activeStages=(bundle.stages||[]).filter(stage=>!stage.deletedAt);
  const stages=new Map(activeStages.map(stage=>[stage.id,stage]));
  const activeWoods=(bundle.woods||[]).filter(wood=>!wood.deletedAt);

  return createPlannerCompatibilityRequirements({
    stageRequirements:(bundle.stageTargets||[])
      .filter(target=>stages.has(target.stageId))
      .map(target=>{
        const stage=stages.get(target.stageId);
        return {
          stageId:target.stageId,
          stageType:stage.typeKey,
          label:stage.nameOverride||stage.typeKey,
          targetChamberCelsius:target.targetChamberCelsius,
          minChamberCelsius:target.minChamberCelsius,
          maxChamberCelsius:target.maxChamberCelsius
        };
      }),
    coldSmokingRequired,
    requestedWoodKeys:activeWoods.map(wood=>wood.woodKey).filter(Boolean),
    productCategories
  });
}
